import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';

const { FiAlertTriangle, FiPhone, FiShield, FiCheck, FiX, FiUsers, FiFileText, FiHeart, FiInfo, FiClock, FiExternalLink, FiSend, FiUser, FiMail, FiBuilding } = FiIcons;

const EmergencyChecklist = ({ onClose, companyId }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [activeTab, setActiveTab] = useState('emergency');
  const [alertSent, setAlertSent] = useState(false);
  const [comments, setComments] = useState({});
  
  const { companies } = useData();
  const { user } = useAuth();
  
  const currentCompany = companies.find(c => c.id === companyId) || companies[0] || { name: 'Ukjent Bedrift' };

  const handleCheck = (id) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCommentChange = (itemId, comment) => {
    setComments(prev => ({ ...prev, [itemId]: comment }));
  };

  const sendEmergencyAlert = () => {
    setAlertSent(true);
    setTimeout(() => setAlertSent(false), 3000);
  };

  const emergencyNumbers = [
    { number: '113', service: 'Medisinsk nødtelefon', description: 'Ambulanse og medisinsk råd', color: 'bg-red-500', icon: FiHeart },
    { number: '110', service: 'Brannvesenet', description: 'Brann og akutte faresituasjoner', color: 'bg-orange-500', icon: FiAlertTriangle },
    { number: '112', service: 'Politi', description: 'Ved alvorlige ulykker og kriminalitet', color: 'bg-blue-500', icon: FiShield }
  ];

  // OPPDATERT LISTE BASERT PÅ BILDET DITT
  const checklistSections = [
    {
      title: '1. Akutt varsling',
      priority: 'critical',
      items: [
        { id: 'medical_emergency', text: 'Kontaktet medisinsk nødtelefon 113', description: 'Medisinsk nødtelefon rekvirerer ambulanse og gir medisinsk råd.', urgent: true },
        { id: 'police', text: 'Kontaktet politi 112', description: 'Ved alvorlig ulykke, skal arbeidsgiver straks varsle nærmeste politimyndighet.', reference: 'AML', urgent: true },
        { id: 'fire_dept', text: 'Kontaktet brannvesenet 110', description: 'Ved brann eller fare for brann/eksplosjon.', urgent: true }
      ]
    },
    {
      title: '2. Intern varsling',
      priority: 'high',
      items: [
        { id: 'daily_manager', text: 'Varslet daglig leder', description: 'Hvis ikke daglig leder er til stede der hendelsen inntraff.', showContact: true },
        { id: 'safety_rep', text: 'Kontaktet verneombud', description: 'Verneombudet skal ha kopi av meldingen som arbeidsgiver har gitt til Politi / Arbeidstilsyn', reference: 'AML' },
        { id: 'union_rep', text: 'Kontaktet tillitsvalgt', description: 'Vurder om det er nødvendig å kontakte bedriftens tillitsvalgte' }
      ]
    },
    {
      title: '3. Myndigheter og tilsyn',
      priority: 'high',
      items: [
        { id: 'labor_inspection', text: 'Kontaktet Arbeidstilsynet', description: 'Dersom arbeidstaker rammes av en alvorlig ulykke, skal arbeidsgiver straks varsle Arbeidstilsynet.', note: 'Definisjon «Alvorlig skade»' },
        { id: 'industry_supervision', text: 'Andre bransjespesifikke tilsyn', description: 'Eks. Direktoratet for samfunnssikkerhet og beredskap (DSB) (ved strømulykke)' }
      ]
    },
    {
      title: '4. Pårørende og oppfølging',
      priority: 'medium',
      items: [
        { id: 'next_of_kin', text: 'Varsling og oppfølging av pårørende', description: 'Sikre at pårørende er raskt informert direkte og ikke via media eller tilfeldig person. Avklare hvem som følger opp fra bedriften.' },
        { id: 'injured_followup', text: 'Oppfølging av den skadde', description: 'Omsorg, om mulig bidra til god behandling, vurder tilrettelagt arbeid' },
        { id: 'colleague_support', text: 'Avklar bruk av kollega(er)', description: 'Avklar om det er aktuelt og hva de kan bidra med' }
      ]
    },
    {
      title: '5. Bedriftshelsetjeneste og støtte',
      priority: 'medium',
      items: [
        { id: 'occupational_health', text: 'Kontaktet bedriftshelsetjenesten', description: 'Avklar hva de kan bidra med, oppfølging av ansatte, ledelse. Sorg/kriseoppfølging' }
      ]
    },
    {
      title: '6. Kommunikasjon',
      priority: 'medium',
      items: [
        { id: 'media_contact', text: 'Mediekontakt', description: 'Hvem uttaler seg på vegne av bedriften til media? Sjekk egen mediepolicy.' },
        { id: 'employee_info', text: 'Informasjon til ansatte', description: 'Lage informasjonsskriv til ansatte? Gjennomføre allmøte?' }
      ]
    }
  ];

  // (Resten av koden for UI er identisk med forrige versjon, men bruker nå den oppdaterte listen)
  // ...
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'medium': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalItems = checklistSections.reduce((acc, section) => acc + section.items.length, 0);
  const completionRate = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" >
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-red-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiAlertTriangle} className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">HMS Sjekkliste og Akuttinformasjon</h2>
              <p className="text-sm text-gray-600"> {currentCompany.name} - Ved ulykker </p>
            </div>
          </div>
          <button onClick={onClose}><SafeIcon icon={FiX} className="text-xl" /></button>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button onClick={() => setActiveTab('emergency')} className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === 'emergency' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500'}`}><SafeIcon icon={FiHeart} /> <span>Akutt hjelp</span></button>
            <button onClick={() => setActiveTab('checklist')} className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === 'checklist' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500'}`}><SafeIcon icon={FiFileText} /> <span>Sjekkliste</span></button>
          </nav>
        </div>

        <div className="p-6 overflow-y-auto bg-gray-50 flex-1">
          {activeTab === 'emergency' && (
            <div className="grid md:grid-cols-3 gap-6">
              {emergencyNumbers.map((emergency, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center shadow-sm">
                  <div className={`w-16 h-16 ${emergency.color} rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl`}><SafeIcon icon={emergency.icon} /></div>
                  <div className="text-3xl font-bold mb-2">{emergency.number}</div>
                  <div className="font-bold mb-2">{emergency.service}</div>
                  <p className="text-sm text-gray-600">{emergency.description}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'checklist' && (
            <div className="space-y-6">
              {alertSent && (
                <div className="bg-green-100 border border-green-300 p-4 rounded-lg flex items-center gap-2 text-green-800">
                  <SafeIcon icon={FiCheck} /> Varsel er loggført og sendt til ledelsen.
                </div>
              )}
              
              {checklistSections.map((section, index) => (
                <div key={index} className={`border-2 rounded-lg p-6 bg-white ${getPriorityColor(section.priority)}`}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiClock} /> {section.title}
                  </h3>
                  <div className="space-y-3">
                    {section.items.map((item) => (
                      <div key={item.id} className="p-3 bg-gray-50 rounded border border-gray-200">
                        <div className="flex items-start gap-3">
                          <button onClick={() => handleCheck(item.id)} className={`w-6 h-6 rounded border flex items-center justify-center ${checkedItems[item.id] ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300'}`}>
                            {checkedItems[item.id] && <SafeIcon icon={FiCheck} size={14} />}
                          </button>
                          <div className="flex-1">
                            <span className={`font-medium block ${checkedItems[item.id] ? 'line-through text-gray-500' : 'text-gray-900'}`}>{item.text}</span>
                            {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                            <textarea 
                              placeholder="Notat..." 
                              className="w-full mt-2 text-xs p-2 border rounded" 
                              rows="1"
                              value={comments[item.id] || ''}
                              onChange={(e) => handleCommentChange(item.id, e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className="flex justify-end pt-4">
                <button onClick={sendEmergencyAlert} className="bg-red-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:bg-red-700">
                  Send statusrapport til ledelsen
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmergencyChecklist;